import { checkPhoneLengthFromMask, useErrorState } from "@finbeat/react-toolkit";
import { getClientLimit, requestClientLimit } from "axios/merchantApi";
import { MerchantOrderModalFormType } from "components/Workplaces/Merchant/MerchantOrderModal/MerchantOrderModal";
import { LIMIT_STATE_DECLINED, LIMIT_STATE_IN_ACTIVE, LIMIT_STATE_RESET_REQUIRED } from "constans/limitStates";
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import { ClientCreditLimit, ScoringClientLimitResponse } from "models/api/merchant";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { UseMutateFunction, useMutation, useQuery } from "react-query";
import { useMerchantWorkplaceState } from "../components/Workplaces/Merchant/Merchant";
import { useNotifyContext } from "./NotifyProvider";

export type MerchantOrderProviderProps = {
    children: React.ReactNode;
};

type FormContextType = {
    clientCreditLimit: ClientCreditLimit,
    clientCreditLimitRequired: boolean;
    clientLimitFetching: boolean;
    waitClientLimitResponse: boolean;
    requestClientLimitLoading: boolean;
    requestClientLimitMutate: UseMutateFunction<number, unknown, void, unknown>;
    tariffGroupId: number | null;
};

const MerchantOrderContext = createContext<FormContextType>({} as FormContextType);

const defaultCreditLimitState: ClientCreditLimit = {
    limitState: LIMIT_STATE_IN_ACTIVE,
    amount: 0,
    term: null,
    blockTerm: null,
    amountRequest: null,
    scoreLimit: null
};

const MerchantOrderProvider = ({
    children
}: MerchantOrderProviderProps) => {
    const api = useApi();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();
    const { currentItem } = useMerchantWorkplaceState();
    const { addSubscription, removeSubscription } = useNotifyContext();

    const { watch } = useFormContext<MerchantOrderModalFormType>();
    const phoneNumber = watch('phoneNumber');
    const tariffGroupId = watch('tariffId');

    const [clientCreditLimit, setClientCreditLimit] =
        useState<ClientCreditLimit>(defaultCreditLimitState);

    const [waitClientLimit, setWaitClientLimit] = useState<boolean>(false);

    const { isFetching, refetch } = useQuery(
        ['merchantClientLimit', phoneNumber],
        () => getClientLimit(api, phoneNumber),
        {
            onSuccess: (data) => setClientCreditLimit({
                ...data
            }),
            onError: (error) => handleRequestError(error),
            enabled: hasRole('merchantGetClientLimit') && currentItem === null && !checkPhoneLengthFromMask(phoneNumber),
        }
    );

    const { isLoading, mutate } = useMutation(() => requestClientLimit(api, phoneNumber), {
        onSuccess: (data) => {
            setWaitClientLimit(true);
            addSubscription({
                key: 'onScoringIsFinished',
                type: 'ScoringIsFinished',
                callback: (message) => {
                    const trans: ScoringClientLimitResponse = JSON.parse(message);
                    if (trans.ClientId !== data) {
                        return;
                    }
                    removeSubscription('onScoringIsFinished');
                    setWaitClientLimit(false);

                    if (trans.ScoringStatus) {
                        refetch();
                    }
                },
            });
        },
        onError: (e) => handleRequestError(e)
    });


    useEffect(() => {
        if (
            checkPhoneLengthFromMask(phoneNumber) &&
            clientCreditLimit !== defaultCreditLimitState
        ) {
            setClientCreditLimit(defaultCreditLimitState);
        }
    }, [phoneNumber]);

    const contextValue = useMemo(() => ({
        clientCreditLimit,
        clientCreditLimitRequired: [LIMIT_STATE_DECLINED, LIMIT_STATE_RESET_REQUIRED].includes(clientCreditLimit.limitState),
        clientLimitFetching: isFetching,
        waitClientLimitResponse: waitClientLimit,
        requestClientLimitLoading: isLoading,
        requestClientLimitMutate: mutate,
        tariffGroupId
    }), [clientCreditLimit, isFetching, waitClientLimit, isLoading, mutate, tariffGroupId]);

    return (
        <MerchantOrderContext.Provider value={contextValue}>
            {children}
        </MerchantOrderContext.Provider>
    );
};


const useMerchantOrderContext = () => useContext<FormContextType>(MerchantOrderContext);

export { MerchantOrderProvider, useMerchantOrderContext };
